import { z } from 'zod';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { PolicyAction, PolicyFormValues } from './policy-dialog';
import SharedAutocomplete from '../../shared/shared-autocomplete';
import AvatarChip from '../../shared/avatar-chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import FormHelperText from '@mui/material/FormHelperText';
interface StepThreeProps {
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  getValues: UseFormGetValues<PolicyFormValues>;
  setValue: UseFormSetValue<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
  trigger: UseFormTrigger<PolicyFormValues>;
}

export const CriteriaModelSchema = z
  .object({
    from: z.string().catch(''),
    to: z.string().catch(''),
    subject: z.string().catch(''),
    query: z.string().catch(''),
    negatedQuery: z.string().catch(''),
    hasAttachment: z.boolean().catch(false),
    excludeChats: z.boolean().catch(false),
    size: z.number().catch(0),
    sizeComparison: z.string().catch(''),
    sizeType: z.string().catch(''),
  })
  .refine(
    (data) => {
      return (
        data.from.trim() !== '' ||
        data.to.trim() !== '' ||
        data.subject.trim() !== '' ||
        data.query.trim() !== '' ||
        data.negatedQuery.trim() !== '' ||
        data.hasAttachment === true ||
        data.excludeChats === true ||
        (data.size > 0 &&
          data.sizeComparison.trim() !== '' &&
          data.sizeType.trim() !== '')
      );
    },
    {
      message: 'At least one filter criteria is required.',
    }
  );

export const ActionModelSchema = z
  .object({
    addLabelIds: z.array(z.string()),
    removeLabelIds: z.array(z.string()),
    forward: z.string().catch(''),
  })
  .refine(
    (data) => {
      return (
        (data.forward && data.forward.trim() !== '') ||
        data.addLabelIds.length > 0 ||
        data.removeLabelIds.length > 0
      );
    },
    {
      message: 'At least one filter action is required.',
    }
  );

export const FilterModelSchema = z.object({
  id: z.string().nullable().optional(),
  criteria: CriteriaModelSchema,
  action: ActionModelSchema,
});

export const CreateGmailFilterActionSchema = z.object({
  type: z.literal('Create_Gmail_Filter'),
  gmailFilter: FilterModelSchema.nullable(),
});

export type CreateGmailFilterActionType = z.infer<
  typeof CreateGmailFilterActionSchema
>;

// Type guard to check if an action is CreateGmailFilterActionType
function isCreateGmailFilterAction(
  action: PolicyAction
): action is CreateGmailFilterActionType {
  return action.type === 'Create_Gmail_Filter';
}

export default function StepThree({
  control,
  watch,
  getValues,
  setValue,
  errors,
  trigger,
}: StepThreeProps) {
  const actionData = watch('actionData');
  const idx = actionData
    .map((action) => action.type)
    .indexOf('Create_Gmail_Filter');
  const actionAtIndex = actionData[idx];
  if (!isCreateGmailFilterAction(actionAtIndex)) {
    return null;
  }

  const currentAction: CreateGmailFilterActionType = actionAtIndex;
  const error: FieldErrors<CreateGmailFilterActionType> | undefined =
    errors?.actionData?.[idx];

  const addLabelIdsValues =
    watch(`actionData.${idx}.gmailFilter.action.addLabelIds`) ?? [];
  const removeLabelIdsValues =
    watch(`actionData.${idx}.gmailFilter.action.removeLabelIds`) ?? [];

  const sizeComparisonOptions = [
    { value: 'larger', displayValue: 'Greater Than' },
    { value: 'smaller', displayValue: 'Less Than' },
  ];

  const sizeTypeOptions = [
    { value: 'MB', displayValue: 'MB' },
    { value: 'KB', displayValue: 'KB' },
    { value: 'Byte', displayValue: 'Byte' },
  ];

  return (
    <Stack>
      <Stack
        flexDirection={'row'}
        justifyContent={'center'}
        sx={{ mt: 2 }}
        gap={3}
      >
        <Stack
          justifyContent={'center'}
          alignItems={'center'}
          gap={1}
          sx={{ mt: 1 }}
        >
          <Stack flexDirection={'row'} gap={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 300, mb: 1 }}>
              Step 1:
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 300, mb: 1 }}>
              Please provide at least one criteria for your gmail filter below.
            </Typography>
          </Stack>
          <Stack
            flexDirection="row"
            sx={{ alignItems: 'flex-start', width: '100%', pl: 4 }}
          >
            <FormHelperText error>
              {error?.gmailFilter?.criteria
                ? error?.gmailFilter.criteria.message
                : ' '}
            </FormHelperText>
          </Stack>
          <Controller
            name={`actionData.${idx}.gmailFilter.criteria.from`}
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'from',
                }}
                InputLabelProps={{
                  htmlFor: 'from',
                }}
                label="From:"
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  trigger(`actionData.${idx}.gmailFilter.criteria`);
                }}
                sx={{ width: 370 }}
                autoComplete="off"
                error={!!error?.gmailFilter?.criteria}
              />
            )}
          />
          <Controller
            name={`actionData.${idx}.gmailFilter.criteria.to`}
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'to',
                }}
                InputLabelProps={{
                  htmlFor: 'to',
                }}
                label="To:"
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  trigger(`actionData.${idx}.gmailFilter.criteria`);
                }}
                sx={{ width: 370 }}
                autoComplete="off"
                error={!!error?.gmailFilter?.criteria}
              />
            )}
          />
          <Controller
            name={`actionData.${idx}.gmailFilter.criteria.subject`}
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'subject',
                }}
                InputLabelProps={{
                  htmlFor: 'subject',
                }}
                label="Subject:"
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                }}
                sx={{ width: 370 }}
                autoComplete="off"
                error={!!error?.gmailFilter?.criteria}
              />
            )}
          />
          <Controller
            name={`actionData.${idx}.gmailFilter.criteria.query`}
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'query',
                }}
                InputLabelProps={{
                  htmlFor: 'query',
                }}
                label="Has the words:"
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                }}
                sx={{ width: 370 }}
                autoComplete="off"
                error={!!error?.gmailFilter?.criteria}
              />
            )}
          />
          <Controller
            name={`actionData.${idx}.gmailFilter.criteria.negatedQuery`}
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'negatedQuery',
                }}
                InputLabelProps={{
                  htmlFor: 'negatedQuery',
                }}
                label="Doesn't have:"
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  trigger(`actionData.${idx}.gmailFilter.criteria`);
                }}
                sx={{ width: 370 }}
                autoComplete="off"
                error={!!error?.gmailFilter?.criteria}
              />
            )}
          />
          <Stack flexDirection="row" gap={2} sx={{ width: 370 }}>
            <Controller
              name={`actionData.${idx}.gmailFilter.criteria.sizeComparison`}
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  inputProps={{
                    id: 'sizeComparison',
                  }}
                  InputLabelProps={{
                    htmlFor: 'sizeComparison',
                  }}
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    trigger(`actionData.${idx}.gmailFilter.criteria`);
                  }}
                  value={field.value ? field.value : 'larger'}
                  label="Size Comparison"
                  sx={{ flexGrow: 1 }}
                  error={!!error?.gmailFilter?.criteria}
                >
                  {sizeComparisonOptions.map((option) => (
                    <MenuItem value={option.value} key={option.value}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name={`actionData.${idx}.gmailFilter.criteria.size`}
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'size',
                    min: 0,
                  }}
                  InputLabelProps={{
                    htmlFor: 'size',
                  }}
                  type="number"
                  label="Size:"
                  FormHelperTextProps={{
                    sx: {
                      position: 'absolute',
                      top: 40,
                    },
                  }}
                  value={field.value}
                  onChange={(event) => {
                    if (event.target.value === '') {
                      field.onChange(event.target.value);
                    } else if (event.target.value === 'e') {
                      field.onChange('');
                    } else {
                      field.onChange(parseInt(event.target.value));
                    }
                    trigger(`actionData.${idx}.gmailFilter.criteria`);
                  }}
                  sx={{ width: 100 }}
                  autoComplete="off"
                  error={!!error?.gmailFilter?.criteria}
                />
              )}
            />
            <Controller
              name={`actionData.${idx}.gmailFilter.criteria.sizeType`}
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  inputProps={{
                    id: 'sizeType',
                  }}
                  InputLabelProps={{
                    htmlFor: 'sizeType',
                  }}
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    trigger(`actionData.${idx}.gmailFilter.criteria`);
                  }}
                  value={field.value ? field.value : 'MB'}
                  label="File Size"
                  sx={{ width: 80 }}
                  error={!!error?.gmailFilter?.criteria}
                >
                  {sizeTypeOptions.map((option) => (
                    <MenuItem value={option.value} key={option.value}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
          <Stack flexDirection="row" gap={2} ml={4.5}>
            <Controller
              name={`actionData.${idx}.gmailFilter.criteria.hasAttachment`}
              control={control}
              render={({ field }) => {
                return (
                  <FormControlLabel
                    control={
                      <Checkbox
                        inputProps={{
                          id: 'hasAttachment',
                        }}
                        onChange={(e) => {
                          setValue(
                            `actionData.${idx}.gmailFilter.criteria.hasAttachment`,
                            e.target.checked
                          );
                          trigger(`actionData.${idx}.gmailFilter.criteria`);
                        }}
                        checked={field.value === true}
                      />
                    }
                    label="Has Attachment"
                  />
                );
              }}
            />
          </Stack>
        </Stack>
        <Divider orientation="vertical" flexItem />
        <Stack gap={1} sx={{ mt: 1 }}>
          <Stack flexDirection={'row'} gap={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 300, mb: 1 }}>
              Step 2:
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 300, mb: 1 }}>
              Please provide at least one action for your gmail filter below.
            </Typography>
          </Stack>
          <Stack
            justifyContent={'flex-start'}
            alignItems={'flex-start'}
            gap={0.5}
          >
            <Typography>Forwarding Address</Typography>
            {currentAction.gmailFilter?.action.forward &&
            currentAction.gmailFilter?.action.forward.trim() !== '' ? (
              <AvatarChip
                type="User"
                email={currentAction.gmailFilter.action.forward}
                onDelete={() =>
                  setValue(`actionData.${idx}.gmailFilter.action.forward`, '')
                }
                size="medium"
              />
            ) : (
              <Controller
                name={`actionData.${idx}.gmailFilter.action.forward`}
                control={control}
                render={({ field }) => (
                  <Box sx={{ width: 400 }}>
                    <SharedAutocomplete
                      sources={['users', 'groups']}
                      autocompleteValue={field.value}
                      setAutoCompleteValue={(e) => {
                        field.onChange(e);
                        trigger(`actionData.${idx}.gmailFilter.action`);
                      }}
                      errors={!!error?.gmailFilter?.action}
                      helperText={
                        error?.gmailFilter?.action
                          ? error?.gmailFilter.action.message
                          : ' '
                      }
                    />
                  </Box>
                )}
              />
            )}
          </Stack>
          <Grid
            alignItems={'center'}
            justifyContent={'center'}
            container
            spacing={1}
            width={500}
          >
            <Grid size={{ xs: 4 }}>
              <Controller
                name={`actionData.${idx}.gmailFilter.action.removeLabelIds`}
                control={control}
                render={({ field }) => {
                  return (
                    <FormControlLabel
                      control={
                        <Checkbox
                          inputProps={{
                            id: 'removeLabelIdsInbox',
                          }}
                          onChange={() => {
                            if (!field.value.includes('INBOX')) {
                              setValue(
                                `actionData.${idx}.gmailFilter.action.removeLabelIds`,
                                [
                                  ...getValues(
                                    `actionData.${idx}.gmailFilter.action.removeLabelIds`
                                  ),
                                  'INBOX',
                                ]
                              );
                            } else {
                              setValue(
                                `actionData.${idx}.gmailFilter.action.removeLabelIds`,
                                getValues(
                                  `actionData.${idx}.gmailFilter.action.removeLabelIds`
                                ).filter((value) => value !== 'INBOX')
                              );
                            }
                            if (addLabelIdsValues.includes('TRASH')) {
                              setValue(
                                `actionData.${idx}.gmailFilter.action.addLabelIds`,
                                getValues(
                                  `actionData.${idx}.gmailFilter.action.addLabelIds`
                                ).filter((value) => value !== 'TRASH')
                              );
                            }
                            trigger(`actionData.${idx}.gmailFilter.action`);
                          }}
                          checked={field.value.includes('INBOX')}
                        />
                      }
                      label="Skip Inbox"
                    />
                  );
                }}
              />
            </Grid>
            <Grid size={{ xs: 5 }}>
              <Controller
                name={`actionData.${idx}.gmailFilter.action.removeLabelIds`}
                control={control}
                render={({ field }) => {
                  return (
                    <FormControlLabel
                      control={
                        <Checkbox
                          inputProps={{
                            id: 'removeLabelIdsUnread',
                          }}
                          onChange={() => {
                            if (!field.value.includes('UNREAD')) {
                              setValue(
                                `actionData.${idx}.gmailFilter.action.removeLabelIds`,
                                [
                                  ...getValues(
                                    `actionData.${idx}.gmailFilter.action.removeLabelIds`
                                  ),
                                  'UNREAD',
                                ]
                              );
                            } else {
                              setValue(
                                `actionData.${idx}.gmailFilter.action.removeLabelIds`,
                                getValues(
                                  `actionData.${idx}.gmailFilter.action.removeLabelIds`
                                ).filter((value) => value !== 'UNREAD')
                              );
                            }
                            trigger(`actionData.${idx}.gmailFilter.action`);
                          }}
                          checked={field.value.includes('UNREAD')}
                        />
                      }
                      label="Mark Email As Read"
                    />
                  );
                }}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Controller
                name={`actionData.${idx}.gmailFilter.action.addLabelIds`}
                control={control}
                render={({ field }) => {
                  return (
                    <FormControlLabel
                      control={
                        <Checkbox
                          inputProps={{
                            id: 'addLabelIdsStarred',
                          }}
                          onChange={() => {
                            if (!field.value.includes('STARRED')) {
                              setValue(
                                `actionData.${idx}.gmailFilter.action.addLabelIds`,
                                [
                                  ...getValues(
                                    `actionData.${idx}.gmailFilter.action.addLabelIds`
                                  ),
                                  'STARRED',
                                ]
                              );
                            } else {
                              setValue(
                                `actionData.${idx}.gmailFilter.action.addLabelIds`,
                                getValues(
                                  `actionData.${idx}.gmailFilter.action.addLabelIds`
                                ).filter((value) => value !== 'STARRED')
                              );
                            }
                            trigger(`actionData.${idx}.gmailFilter.action`);
                          }}
                          checked={field.value.includes('STARRED')}
                        />
                      }
                      label="Star Email"
                    />
                  );
                }}
              />
            </Grid>
            <Grid size={{ xs: 5 }}>
              <Controller
                name={`actionData.${idx}.gmailFilter.action.addLabelIds`}
                control={control}
                render={({ field }) => {
                  return (
                    <FormControlLabel
                      control={
                        <Checkbox
                          inputProps={{
                            id: 'addLabelIdsTrash',
                          }}
                          onChange={() => {
                            if (!field.value.includes('TRASH')) {
                              setValue(
                                `actionData.${idx}.gmailFilter.action.addLabelIds`,
                                [
                                  ...getValues(
                                    `actionData.${idx}.gmailFilter.action.addLabelIds`
                                  ),
                                  'TRASH',
                                ]
                              );
                            } else {
                              setValue(
                                `actionData.${idx}.gmailFilter.action.addLabelIds`,
                                getValues(
                                  `actionData.${idx}.gmailFilter.action.addLabelIds`
                                ).filter((value) => value !== 'TRASH')
                              );
                            }
                            if (removeLabelIdsValues.includes('INBOX')) {
                              setValue(
                                `actionData.${idx}.gmailFilter.action.removeLabelIds`,
                                getValues(
                                  `actionData.${idx}.gmailFilter.action.removeLabelIds`
                                ).filter((value) => value !== 'INBOX')
                              );
                            }
                            trigger(`actionData.${idx}.gmailFilter.action`);
                          }}
                          checked={field.value.includes('TRASH')}
                        />
                      }
                      label="Trash Email"
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Stack>
      </Stack>
    </Stack>
  );
}
